import { INotification } from 'services/notifications/types'

const SERVICE_ALIASES: Record<string, string> = {
  call: 'service_calls',
  service_call: 'service_calls',
  service_calls: 'service_calls',
  task: 'tasks',
  task_v2: 'tasks',
  tasks_v2: 'tasks',
  tasks: 'tasks',
  post: 'posts',
  posts: 'posts',
  pop: 'pops',
  pops: 'pops',
  comment: 'comments',
  comments: 'comments',
  mention: 'mentions',
  shift: 'shifts',
}

const normalizeService = (service: string | undefined): string => {
  if (!service) return 'unknown'
  const normalized = service.trim().toLowerCase()
  return SERVICE_ALIASES[normalized] ?? normalized
}

const firstMeaningfulString = (...values: unknown[]): string | undefined => {
  const value = values.find(
    (candidate) => typeof candidate === 'string' && candidate.trim() && candidate.trim().toLowerCase() !== 'unknown'
  )
  return typeof value === 'string' ? value.trim() : undefined
}

const toUnixSeconds = (value: unknown): number => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    if (!Number.isNaN(parsed)) return Math.floor(parsed / 1000)
  }
  return Math.floor(Date.now() / 1000)
}

// Suffix for a chrono_key/notif_id we generate locally below, only when the raw payload carried no
// real backend id at all — e.g. a live web-push message whose JSON never included one (confirmed
// happening for at least `posts`/`post_created` pushes). A real backend chrono_key is always
// `{ts}#{uuid}`, which never happens to end in this literal word, so a plain suffix check is safe.
const SYNTHETIC_ID_SUFFIX = '#notification'

// True for an id we fabricated client-side rather than one the backend actually issued. Never send
// one of these to an endpoint that expects a real chrono_key (e.g. mark-seen) — there is no
// matching record server-side and the request 500s.
export const isSyntheticChronoKey = (key: string | null | undefined): boolean =>
  typeof key === 'string' && key.endsWith(SYNTHETIC_ID_SUFFIX)

// Accepts both the notifications-ms shape already used by the app and the WA-931 design-contract
// shape (`id`, `createdAt`, `read`, `target`) so rendering/routing does not depend on one backend
// naming convention leaking through every component.
export function normalizeNotification(raw: any): INotification {
  const payload = raw?.data && typeof raw.data === 'object' ? raw.data : {}
  const createdAtTs = toUnixSeconds(raw?.created_at_ts ?? raw?.createdAt)
  // `unread_sk` is backend's actual mark-seen key (same field GET /unread returns it under) — a
  // browser push payload carries it even though it omits notif_id/chrono_key entirely. Check for it
  // first; only fabricate the synthetic fallback id when it's genuinely absent.
  const unreadSk = firstMeaningfulString(raw?.unread_sk, payload?.unread_sk)
  const id = String(unreadSk ?? raw?.notif_id ?? raw?.id ?? raw?.chrono_key ?? `${createdAtTs}${SYNTHETIC_ID_SUFFIX}`)
  const target = raw?.target ?? payload?.target

  return {
    ...raw,
    notif_id: id,
    chrono_key: String(unreadSk ?? raw?.chrono_key ?? raw?.id ?? id),
    service: normalizeService(
      firstMeaningfulString(
        raw?.service,
        payload?.service,
        raw?.service_type,
        payload?.service_type,
        raw?.serviceType,
        payload?.serviceType
      )
    ),
    sender_id: firstMeaningfulString(raw?.sender_id, payload?.sender_id, raw?.senderId, payload?.senderId) ?? '',
    event_type: firstMeaningfulString(raw?.event_type, payload?.event_type, raw?.eventType, payload?.eventType) ?? '',
    title: String(raw?.title ?? payload?.title ?? 'New Notification'),
    title_translation_key:
      firstMeaningfulString(
        raw?.title_translation_key,
        payload?.title_translation_key,
        raw?.titleTranslationKey,
        payload?.titleTranslationKey,
        raw?.title_key,
        payload?.title_key
      ) ?? null,
    body: raw?.body ?? payload?.body ?? null,
    object_ref: raw?.object_ref ?? payload?.object_ref ?? raw?.objectRef ?? payload?.objectRef ?? null,
    data: raw?.data ?? null,
    seen: Boolean(raw?.seen ?? raw?.read ?? false),
    seen_at_ts: raw?.seen_at_ts ?? raw?.seenAtTs ?? null,
    created_at_ts: createdAtTs,
    ctaLabel: raw?.ctaLabel ?? payload?.ctaLabel ?? target?.label,
    target,
    preview: raw?.preview ?? payload?.preview,
  }
}

export function normalizeNotificationList<
  T extends { items: any[]; next_cursor?: string | null; nextCursor?: string | null }
>(list: T) {
  return {
    items: (list.items ?? []).map(normalizeNotification),
    next_cursor: list.next_cursor ?? list.nextCursor ?? null,
  }
}
